/**
 * Travel time estimation utilities.
 * Extracted from WanderAppComplete.jsx for shared use by web and mobile.
 *
 * These are fallback estimators used when real-time data (Google Maps, etc.)
 * is unavailable. They provide reasonable defaults for:
 * - Flight-to-drive-time conversion
 * - Intra-city/inter-location transit estimates
 * - Arrival buffer calculations
 */

/**
 * Estimate driving hours based on flight duration.
 * Uses empirical scaling factors that increase with distance.
 *
 * @param {number} flightHours - Estimated flight time in hours
 * @returns {number} Estimated driving hours (rounded)
 */
export function estimateDriveHours(flightHours) {
  if (!flightHours || flightHours <= 0) return 4;
  if (flightHours <= 1) return Math.round(flightHours * 5);       // 1hr flight ≈ 5hr drive
  if (flightHours <= 1.5) return Math.round(flightHours * 4.5);   // 1.5hr flight ≈ 7hr drive
  if (flightHours <= 2) return Math.round(flightHours * 5);       // 2hr flight ≈ 10hr drive
  if (flightHours <= 2.5) return Math.round(flightHours * 5.5);   // 2.5hr flight ≈ 14hr drive
  return Math.round(flightHours * 6);                              // 3hr+ flight = too far to drive realistically
}

/**
 * Great-circle ("as the crow flies") distance in miles between two
 * lat/lon pairs using the haversine formula.
 *
 * @param {{lat:number, lon:number}} a
 * @param {{lat:number, lon:number}} b
 * @returns {number} Distance in miles
 */
export function greatCircleMiles(a, b) {
  if (!a || !b || typeof a.lat !== 'number' || typeof b.lat !== 'number') return 0;
  const R = 3958.7613; // Earth radius in miles
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Sanity-check a reported drive duration against geography.
 *
 * Returns the reported duration only if it is plausible given the
 * great-circle distance between origin and destination. Roads are never
 * shorter than the great-circle distance and usable long-distance driving
 * speeds average ~60 mph (with mountains, traffic and legal limits). If
 * the reported duration is more than 2× faster than 60 mph over the
 * great-circle distance, treat it as bogus and fall back to a static
 * estimate (great-circle / 55 mph).
 *
 * Used for routes like Park City, UT → Aspen, CO where a geocoder or
 * Directions API can occasionally return absurd figures (~2h 45m) for
 * what is really a ~7h mountain drive (~530mi road, ~280mi crow-fly).
 *
 * @param {number} reportedHours - Hours returned by upstream API.
 * @param {{lat:number, lon:number}} from - Origin coordinates
 * @param {{lat:number, lon:number}} to - Destination coordinates
 * @param {Object} [opts]
 * @param {number} [opts.maxMph=60] - Expected sustained average speed (mph)
 * @param {number} [opts.toleranceFactor=2] - Reject if reported is more than
 *   toleranceFactor× faster than the great-circle/maxMph baseline.
 * @param {number} [opts.roadDetour=1.3] - Typical ratio of road miles to
 *   great-circle miles (mountain/interstate mix).
 * @returns {{hours:number, source:'reported'|'fallback', reason?:string}}
 */
export function sanityCheckDriveHours(reportedHours, from, to, opts = {}) {
  const { maxMph = 60, toleranceFactor = 2, roadDetour = 1.3 } = opts;
  const crowMiles = greatCircleMiles(from, to);
  // Without geo coords we can't sanity-check — pass through.
  if (!crowMiles || crowMiles <= 0) {
    return { hours: reportedHours, source: 'reported' };
  }
  // Real roads cover roadDetour× the great-circle distance. Minimum
  // plausible driving hours at maxMph average is (crow × roadDetour) /
  // maxMph. Anything more than toleranceFactor× faster than that is
  // physically impossible for normal road travel.
  const realisticHours = (crowMiles * roadDetour) / maxMph;
  const minPlausibleHours = realisticHours / toleranceFactor;
  if (typeof reportedHours === 'number' && reportedHours >= minPlausibleHours) {
    return { hours: reportedHours, source: 'reported' };
  }
  // Fall back to a realistic estimate: road miles ≈ crow × roadDetour at 55 mph.
  const fallback = (crowMiles * roadDetour) / 55;
  return {
    hours: fallback,
    source: 'fallback',
    reason: `reported ${reportedHours}h is faster than the ${minPlausibleHours.toFixed(1)}h floor for ${crowMiles.toFixed(0)} mi crow-fly`,
  };
}

/**
 * Estimate travel time between two locations.
 * Smart fallback when Google Maps API call fails or is unavailable.
 * Categorizes by trip type (island, park, airport, city) to return
 * realistic durations.
 *
 * @param {string} origin - Origin location name
 * @param {string} destination - Destination location name
 * @param {'driving'|'transit'|'walking'} [mode='driving'] - Travel mode
 * @returns {{ duration: string, durationMinutes: number, distance: string, mode: string, estimated: boolean }}
 */
export function estimateTravelTime(origin, destination, mode = 'driving') {
  const originLower = (origin || '').toLowerCase();
  const destLower = (destination || '').toLowerCase();

  // Check if this involves an airport
  const isAirportTransfer = originLower.includes('airport') || destLower.includes('airport') ||
                             originLower.includes('terminal') || destLower.includes('terminal');

  // Check if this is a park/nature area (scenic routes often have longer drives)
  const isParkArea = originLower.includes('national park') || destLower.includes('national park') ||
                     originLower.includes(' park,') || destLower.includes(' park,') ||
                     originLower.includes(' park ') || destLower.includes(' park ') ||
                     originLower.includes('trail') || destLower.includes('trail') ||
                     originLower.includes('trailhead') || destLower.includes('trailhead') ||
                     originLower.includes('visitor center') || destLower.includes('visitor center');

  // Check for islands and remote destinations
  const islandKeywords = ['island', 'cay', 'key', 'isla', 'isle', 'atoll', 'roatan', 'cozumel', 'aruba',
                          'bahamas', 'maldives', 'bora bora', 'fiji', 'tahiti', 'seychelles', 'mauritius',
                          'zanzibar', 'santorini', 'mykonos', 'capri', 'bermuda', 'cayman', 'turks', 'virgin'];
  const isIsland = islandKeywords.some(keyword =>
    originLower.includes(keyword) || destLower.includes(keyword)
  );

  // Check if this is a restaurant/café/meal (usually close to previous activity)
  const mealKeywords = ['restaurant', 'café', 'cafe', 'bistro', 'trattoria', 'brasserie', 'ristorante',
                        'taverna', 'pizzeria', 'bakery', 'diner', 'bar ', 'pub ', 'grill', 'steakhouse',
                        'sushi', 'ramen', 'taco', 'brunch', 'breakfast', 'lunch', 'dinner'];
  const isMealTransit = mealKeywords.some(k => originLower.includes(k) || destLower.includes(k));

  // Simple string hash for deterministic variation (avoids all fallbacks being identical)
  const _hashStr = (origin || '') + (destination || '');
  let _hash = 0;
  for (let i = 0; i < _hashStr.length; i++) _hash = ((_hash << 5) - _hash + _hashStr.charCodeAt(i)) | 0;
  const _variation = (Math.abs(_hash) % 11) - 5; // -5 to +5 minute variation

  // Base times vary by trip type
  let baseMinutes;

  if (isIsland && isAirportTransfer) {
    baseMinutes = { driving: 25, transit: 40, walking: 60 };
  } else if (isIsland) {
    baseMinutes = { driving: 15, transit: 30, walking: 25 };
  } else if (isAirportTransfer && isParkArea) {
    baseMinutes = { driving: 60, transit: 90, walking: 180 };
  } else if (isParkArea) {
    baseMinutes = { driving: 30, transit: 45, walking: 60 };
  } else if (isAirportTransfer) {
    baseMinutes = { driving: 30, transit: 45, walking: 120 };
  } else if (isMealTransit) {
    // Meals are usually near the previous activity (5-12 min)
    baseMinutes = { driving: 8, transit: 12, walking: 10 };
  } else {
    // Generic intra-city transit (10-20 min with variation)
    baseMinutes = { driving: 15, transit: 22, walking: 18 };
  }

  const rawMinutes = baseMinutes[mode] || baseMinutes.driving;
  const minutes = Math.max(3, rawMinutes + _variation); // add variation, min 3 min

  // Format duration nicely
  const formatDuration = (mins) => {
    if (mins >= 9999) return 'N/A';
    if (mins >= 60) {
      const hours = Math.floor(mins / 60);
      const remainingMins = mins % 60;
      return remainingMins > 0 ? `${hours} hr ${remainingMins} mins` : `${hours} hr`;
    }
    return `${mins} min`;
  };

  return {
    duration: formatDuration(minutes),
    durationMinutes: minutes,
    distance: mode === 'walking' ? `${(minutes * 0.05).toFixed(1)} km` : `${(minutes * 0.5).toFixed(1)} km`,
    mode: mode,
    estimated: true
  };
}

// ─── City coordinates (minimal, for short-hop drive detection) ─────────────
// Used by `shouldForceDrive` to catch LLM-hallucinated flights between
// nearby stops (e.g., Napa → Taos "flight" which is 700+ road miles
// mostly useless without real airports at either end, or the user's
// Auberge → Taos case where Auberge is a resort without an airport).
//
// This is intentionally small and covers cities that commonly appear as
// stops in the trip builder and whose distances drive Task C's 250-mile
// threshold. Add more entries as new stop types show up in QA.
export const CITY_COORDS = {
  // USA — mountain / resort / southwest (Task C primary targets)
  'aspen': { lat: 39.1911, lon: -106.8175 },
  'snowmass': { lat: 39.2130, lon: -106.9378 },
  'vail': { lat: 39.6403, lon: -106.3742 },
  'beaver creek': { lat: 39.6042, lon: -106.5170 },
  'breckenridge': { lat: 39.4817, lon: -106.0384 },
  'telluride': { lat: 37.9375, lon: -107.8123 },
  'park city': { lat: 40.6461, lon: -111.4980 },
  'deer valley': { lat: 40.6304, lon: -111.4881 },
  'salt lake city': { lat: 40.7608, lon: -111.8910 },
  'jackson hole': { lat: 43.4799, lon: -110.7624 },
  'big sky': { lat: 45.2841, lon: -111.4058 },
  'bozeman': { lat: 45.6770, lon: -111.0429 },
  'santa fe': { lat: 35.6870, lon: -105.9378 },
  'taos': { lat: 36.4072, lon: -105.5731 },
  'albuquerque': { lat: 35.0844, lon: -106.6504 },
  'sedona': { lat: 34.8697, lon: -111.7609 },
  'flagstaff': { lat: 35.1983, lon: -111.6513 },
  'moab': { lat: 38.5733, lon: -109.5498 },
  'zion': { lat: 37.2982, lon: -113.0263 },
  'springdale': { lat: 37.1883, lon: -112.9990 },
  'las vegas': { lat: 36.1699, lon: -115.1398 },
  'denver': { lat: 39.7392, lon: -104.9903 },
  'napa': { lat: 38.2975, lon: -122.2869 },
  'rutherford': { lat: 38.4630, lon: -122.4197 },
  'sonoma': { lat: 38.2919, lon: -122.4580 },
  'san francisco': { lat: 37.7749, lon: -122.4194 },
  'auberge du soleil': { lat: 38.4657, lon: -122.4061 }, // Rutherford, CA
  // USA — major hubs (used to whitelist "both are real airport cities")
  'new york': { lat: 40.7128, lon: -74.0060 },
  'los angeles': { lat: 34.0522, lon: -118.2437 },
  'chicago': { lat: 41.8781, lon: -87.6298 },
  'miami': { lat: 25.7617, lon: -80.1918 },
  'seattle': { lat: 47.6062, lon: -122.3321 },
  'boston': { lat: 42.3601, lon: -71.0589 },
  'dallas': { lat: 32.7767, lon: -96.7970 },
  'atlanta': { lat: 33.7490, lon: -84.3880 },
  'washington': { lat: 38.9072, lon: -77.0369 },
  'washington dc': { lat: 38.9072, lon: -77.0369 },

  // ── International hubs (for flight duration validation) ──
  // Coords let flightDurationValidator catch LLM hallucinations like
  // "JFK → KIX in 1.5 hours" (real ~13h). Add new cities here any time
  // the validator is seeing `valid: null` in production logs.
  // Asia
  'tokyo': { lat: 35.6762, lon: 139.6503 },
  'osaka': { lat: 34.6937, lon: 135.5023 },
  'kyoto': { lat: 35.0116, lon: 135.7681 },
  'sapporo': { lat: 43.0621, lon: 141.3544 },
  'fukuoka': { lat: 33.5904, lon: 130.4017 },
  'nara': { lat: 34.6851, lon: 135.8048 },
  'seoul': { lat: 37.5665, lon: 126.9780 },
  'busan': { lat: 35.1796, lon: 129.0756 },
  'beijing': { lat: 39.9042, lon: 116.4074 },
  'shanghai': { lat: 31.2304, lon: 121.4737 },
  'hong kong': { lat: 22.3193, lon: 114.1694 },
  'taipei': { lat: 25.0330, lon: 121.5654 },
  'singapore': { lat: 1.3521, lon: 103.8198 },
  'bangkok': { lat: 13.7563, lon: 100.5018 },
  'phuket': { lat: 7.8804, lon: 98.3923 },
  'chiang mai': { lat: 18.7061, lon: 98.9817 },
  'bali': { lat: -8.3405, lon: 115.0920 },
  'jakarta': { lat: -6.2088, lon: 106.8456 },
  'ho chi minh': { lat: 10.8231, lon: 106.6297 },
  'hanoi': { lat: 21.0285, lon: 105.8542 },
  'kuala lumpur': { lat: 3.1390, lon: 101.6869 },
  'manila': { lat: 14.5995, lon: 120.9842 },
  'mumbai': { lat: 19.0760, lon: 72.8777 },
  'delhi': { lat: 28.7041, lon: 77.1025 },
  'bangalore': { lat: 12.9716, lon: 77.5946 },
  'dubai': { lat: 25.2048, lon: 55.2708 },
  'abu dhabi': { lat: 24.4539, lon: 54.3773 },
  // Europe
  'london': { lat: 51.5074, lon: -0.1278 },
  'paris': { lat: 48.8566, lon: 2.3522 },
  'rome': { lat: 41.9028, lon: 12.4964 },
  // Italian cities — needed so the alt-airport radius search lights up
  // when user picks Bologna/Florence/etc. Bologna alone was the trigger
  // (user couldn't switch from BLQ to Milan/Florence/Pisa). Add the
  // common neighbours too.
  'bologna': { lat: 44.4949, lon: 11.3426 },
  'florence': { lat: 43.7696, lon: 11.2558 },
  'firenze': { lat: 43.7696, lon: 11.2558 },
  'venice': { lat: 45.4408, lon: 12.3155 },
  'venezia': { lat: 45.4408, lon: 12.3155 },
  'milan': { lat: 45.4642, lon: 9.1900 },
  'milano': { lat: 45.4642, lon: 9.1900 },
  'naples': { lat: 40.8518, lon: 14.2681 },
  'napoli': { lat: 40.8518, lon: 14.2681 },
  'pisa': { lat: 43.7228, lon: 10.4017 },
  'verona': { lat: 45.4384, lon: 10.9916 },
  'turin': { lat: 45.0703, lon: 7.6869 },
  'genoa': { lat: 44.4056, lon: 8.9463 },
  // Mexico — second cities for the same reason (CDMX→Oaxaca didn't
  // resolve correctly in transport heuristics).
  'oaxaca': { lat: 17.0732, lon: -96.7266 },
  'puebla': { lat: 19.0414, lon: -98.2063 },
  'guadalajara': { lat: 20.6597, lon: -103.3496 },
  'merida': { lat: 20.9674, lon: -89.5926 },
  'tulum': { lat: 20.2114, lon: -87.4654 },
  'puerto vallarta': { lat: 20.6534, lon: -105.2253 },
  'san miguel de allende': { lat: 20.9145, lon: -100.7430 },
  'madrid': { lat: 40.4168, lon: -3.7038 },
  'barcelona': { lat: 41.3851, lon: 2.1734 },
  'berlin': { lat: 52.5200, lon: 13.4050 },
  'munich': { lat: 48.1351, lon: 11.5820 },
  'amsterdam': { lat: 52.3676, lon: 4.9041 },
  'vienna': { lat: 48.2082, lon: 16.3738 },
  'zurich': { lat: 47.3769, lon: 8.5417 },
  'geneva': { lat: 46.2044, lon: 6.1432 },
  'prague': { lat: 50.0755, lon: 14.4378 },
  'budapest': { lat: 47.4979, lon: 19.0402 },
  'copenhagen': { lat: 55.6761, lon: 12.5683 },
  'stockholm': { lat: 59.3293, lon: 18.0686 },
  'oslo': { lat: 59.9139, lon: 10.7522 },
  'helsinki': { lat: 60.1699, lon: 24.9384 },
  'reykjavik': { lat: 64.1466, lon: -21.9426 },
  'dublin': { lat: 53.3498, lon: -6.2603 },
  'edinburgh': { lat: 55.9533, lon: -3.1883 },
  'lisbon': { lat: 38.7223, lon: -9.1393 },
  'porto': { lat: 41.1579, lon: -8.6291 },
  'athens': { lat: 37.9838, lon: 23.7275 },
  'istanbul': { lat: 41.0082, lon: 28.9784 },
  'moscow': { lat: 55.7558, lon: 37.6173 },
  // Americas
  'toronto': { lat: 43.6532, lon: -79.3832 },
  'vancouver': { lat: 49.2827, lon: -123.1207 },
  'montreal': { lat: 45.5017, lon: -73.5673 },
  'quebec': { lat: 46.8139, lon: -71.2080 },
  'quebec city': { lat: 46.8139, lon: -71.2080 },
  'calgary': { lat: 51.0447, lon: -114.0719 },
  'mexico city': { lat: 19.4326, lon: -99.1332 },
  'cancun': { lat: 21.1619, lon: -86.8515 },
  // Caribbean (resort destinations frequently appearing in beach trips)
  'antigua': { lat: 17.0608, lon: -61.7964 },
  'hermitage bay': { lat: 17.1019, lon: -61.8862 }, // resort on Antigua's west coast
  'jumby bay': { lat: 17.1389, lon: -61.7833 },
  'st johns antigua': { lat: 17.1175, lon: -61.8456 },
  'nassau': { lat: 25.0443, lon: -77.3504 },
  'paradise island': { lat: 25.0834, lon: -77.3186 },
  'aruba': { lat: 12.5211, lon: -69.9683 },
  'curacao': { lat: 12.1696, lon: -68.9900 },
  'barbados': { lat: 13.1939, lon: -59.5432 },
  'saint lucia': { lat: 13.9094, lon: -60.9789 },
  'st lucia': { lat: 13.9094, lon: -60.9789 },
  'grenada': { lat: 12.1165, lon: -61.6790 },
  'turks and caicos': { lat: 21.6940, lon: -71.7979 },
  'providenciales': { lat: 21.7736, lon: -72.2683 },
  'grand cayman': { lat: 19.3133, lon: -81.2546 },
  'cayman islands': { lat: 19.3133, lon: -81.2546 },
  'tortola': { lat: 18.4207, lon: -64.6399 },
  'st thomas': { lat: 18.3381, lon: -64.8941 },
  'st barths': { lat: 17.9000, lon: -62.8333 },
  'st maarten': { lat: 18.0425, lon: -63.0548 },
  'punta cana': { lat: 18.5601, lon: -68.3725 },
  'montego bay': { lat: 18.4762, lon: -77.8939 },
  'ocho rios': { lat: 18.4076, lon: -77.1031 },
  'sandals royal bahamian': { lat: 25.0830, lon: -77.3140 }, // Cable Beach, Nassau
  'fairmont mayakoba': { lat: 20.7016, lon: -86.9989 }, // Riviera Maya, MX
  'banyan tree mayakoba': { lat: 20.7016, lon: -86.9989 },
  'buenos aires': { lat: -34.6037, lon: -58.3816 },
  'sao paulo': { lat: -23.5505, lon: -46.6333 },
  'rio de janeiro': { lat: -22.9068, lon: -43.1729 },
  'rio': { lat: -22.9068, lon: -43.1729 },
  'santiago': { lat: -33.4489, lon: -70.6693 },
  'lima': { lat: -12.0464, lon: -77.0428 },
  'cusco': { lat: -13.5320, lon: -71.9675 },
  'bogota': { lat: 4.7110, lon: -74.0721 },
  'cartagena': { lat: 10.3910, lon: -75.4794 },
  'honolulu': { lat: 21.3099, lon: -157.8581 },
  // Africa
  'cairo': { lat: 30.0444, lon: 31.2357 },
  'cape town': { lat: -33.9249, lon: 18.4241 },
  'johannesburg': { lat: -26.2041, lon: 28.0473 },
  'marrakech': { lat: 31.6295, lon: -7.9811 },
  'casablanca': { lat: 33.5731, lon: -7.5898 },
  'nairobi': { lat: -1.2921, lon: 36.8219 },
  // Oceania
  'sydney': { lat: -33.8688, lon: 151.2093 },
  'melbourne': { lat: -37.8136, lon: 144.9631 },
  'brisbane': { lat: -27.4698, lon: 153.0251 },
  'perth': { lat: -31.9505, lon: 115.8605 },
  'newcastle': { lat: -32.9283, lon: 151.7817 }, // Newcastle, NSW, Australia (NTL)
  'hunter valley': { lat: -32.7680, lon: 151.3000 }, // near Newcastle NSW
  'auckland': { lat: -36.8485, lon: 174.7633 },
  'queenstown': { lat: -45.0312, lon: 168.6626 },
  'wellington': { lat: -41.2865, lon: 174.7762 },
  'christchurch': { lat: -43.5321, lon: 172.6362 },
  'rotorua': { lat: -38.1368, lon: 176.2497 },
  'hamilton': { lat: -37.7870, lon: 175.2793 }, // Hamilton, NZ (LLM mentions for NZ multi-city trips)
  'fiji': { lat: -17.7134, lon: 178.0650 },
  'bora bora': { lat: -16.5004, lon: -151.7415 },
  // Asia-Pacific (PR #232 / round-1 audit gaps)
  'macau': { lat: 22.1987, lon: 113.5439 },
  'taipei': { lat: 25.0330, lon: 121.5654 },
  // Italy secondary cities (added with secondary airport map above)
  'catania': { lat: 37.5079, lon: 15.0830 },
  'palermo': { lat: 38.1157, lon: 13.3613 },
  'bari': { lat: 41.1171, lon: 16.8719 },
  // Spain secondary
  'málaga': { lat: 36.7213, lon: -4.4214 },
  'malaga': { lat: 36.7213, lon: -4.4214 },
  'seville': { lat: 37.3891, lon: -5.9845 },
  'bilbao': { lat: 43.2630, lon: -2.9350 },
  'valencia': { lat: 39.4699, lon: -0.3763 },
  // Germany secondary
  'hamburg': { lat: 53.5511, lon: 9.9937 },
  'düsseldorf': { lat: 51.2277, lon: 6.7735 },
  'dusseldorf': { lat: 51.2277, lon: 6.7735 },
  'cologne': { lat: 50.9375, lon: 6.9603 },
  'stuttgart': { lat: 48.7758, lon: 9.1829 },
  // UK secondary cities (DEPARTURE_AIRPORT_CITY_MAP gained MAN/EDI/GLA/etc.
  // but coords were missing — heuristic was falling to ~4h continental default
  // for intra-UK routes like Manchester→London).
  'manchester': { lat: 53.4808, lon: -2.2426 },
  'glasgow': { lat: 55.8642, lon: -4.2518 },
  'liverpool': { lat: 53.4084, lon: -2.9916 },
  'belfast': { lat: 54.5973, lon: -5.9301 },
  'birmingham': { lat: 52.4862, lon: -1.8904 },
  'bristol': { lat: 51.4545, lon: -2.5879 },
  // NZ secondary (round-1 audit gap — WLG → AKL fell to ~4h before this)
  'dunedin': { lat: -45.8788, lon: 170.5028 },
  'napier': { lat: -39.4928, lon: 176.9120 },
  'nelson': { lat: -41.2706, lon: 173.2840 },
  'tauranga': { lat: -37.6878, lon: 176.1651 },
  'palmerston north': { lat: -40.3523, lon: 175.6082 },
  'blenheim': { lat: -41.5134, lon: 173.9612 },
  // Mexico secondary
  'toluca': { lat: 19.2826, lon: -99.6557 },
  'bajío': { lat: 21.0190, lon: -101.4814 }, // León/Guanajuato
  'bajio': { lat: 21.0190, lon: -101.4814 },
};

/**
 * Look up coords for a city name (forgiving of comma suffixes and
 * common resort-name prefixes).
 *
 * @param {string} cityName
 * @returns {{lat:number, lon:number}|null}
 */
export function getCityCoords(cityName) {
  if (!cityName) return null;
  const lower = String(cityName).toLowerCase().trim();
  if (CITY_COORDS[lower]) return CITY_COORDS[lower];
  // Try first comma-separated part (e.g., "Aspen, Colorado" → "aspen")
  const firstPart = lower.split(',')[0].trim();
  if (CITY_COORDS[firstPart]) return CITY_COORDS[firstPart];
  // Try substring match (e.g., "Auberge du Soleil, Rutherford, CA" →
  // match "auberge du soleil" or "rutherford")
  for (const key of Object.keys(CITY_COORDS)) {
    if (lower.includes(key)) return CITY_COORDS[key];
  }
  return null;
}

/**
 * TASK C guard: decide whether a supposed inter-city flight leg should
 * actually be rendered as a drive.
 *
 * Returns true when BOTH cities have known coordinates, their great-circle
 * distance is under `maxMiles`, AND at least one of them is not a known
 * major airport city (i.e., there's no reasonable commercial flight
 * between them). This catches LLM hallucinations like "Auberge →
 * Taos · 4h 30m" where Auberge is a Napa Valley resort, not an airport.
 *
 * If either city's coords are unknown, returns false (pass-through) —
 * we don't want to break genuine long-haul flights by defaulting to
 * drive just because we don't have lat/lon for the stop.
 *
 * @param {string} fromCity
 * @param {string} toCity
 * @param {Object} [opts]
 * @param {number} [opts.maxMiles=250] - Distance threshold
 * @param {Set<string>} [opts.majorAirportCities] - Lowercased set of
 *   cities whose resolvedLocation IS a major airport (e.g., "new york",
 *   "chicago"). When both endpoints are in this set, we let the flight
 *   through even if distance < maxMiles.
 * @returns {{ shouldForceDrive: boolean, miles: number, reason?: string }}
 */
export function shouldForceDrive(fromCity, toCity, opts = {}) {
  const { maxMiles = 250, majorAirportCities } = opts;
  const a = getCityCoords(fromCity);
  const b = getCityCoords(toCity);
  if (!a || !b) {
    return { shouldForceDrive: false, miles: 0 };
  }
  const miles = greatCircleMiles(a, b);
  if (!miles || miles >= maxMiles) {
    return { shouldForceDrive: false, miles };
  }
  // Both cities major airport hubs? Allow the flight (short-haul commuter
  // routes are legitimate: e.g., LAX ↔ SAN, BOS ↔ NYC).
  if (majorAirportCities) {
    const fromLower = String(fromCity || '').toLowerCase().split(',')[0].trim();
    const toLower = String(toCity || '').toLowerCase().split(',')[0].trim();
    if (majorAirportCities.has(fromLower) && majorAirportCities.has(toLower)) {
      return { shouldForceDrive: false, miles };
    }
  }
  return {
    shouldForceDrive: true,
    miles,
    reason: `${miles.toFixed(0)}mi crow-fly between ${fromCity} and ${toCity} is below ${maxMiles}mi threshold`,
  };
}

/**
 * Calculate the earliest reasonable start time for activities after arrival.
 * Adds a 2.5-hour buffer for customs + travel to hotel + check-in.
 *
 * @param {string} arrivalTime - Arrival time in HH:MM 24-hour format
 * @returns {string} Earliest activity start time in HH:MM format
 */
export function calculateStartTime(arrivalTime) {
  if (!arrivalTime) return '12:00';
  const [hours, minutes] = arrivalTime.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return '12:00';
  // Add 1 hour for customs + 1 hour for travel to hotel + 30 min for check-in
  const totalMinutes = hours * 60 + minutes + 150; // 2.5 hours buffer
  const newHours = Math.floor(totalMinutes / 60);
  const newMinutes = totalMinutes % 60;
  return `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
}
